import { computed, shallowRef, useHost } from "vue";
import { useSharedState } from "composables/useSharedState";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";
import { closestAcrossShadowBoundaries } from "@/runtime/shared/shadow-dom";
import type { TabContentProps } from "./tab.types";
import { tabContentClasses } from "./tab.styles";
import { getTabActiveValue } from "./useTab";

export interface UseTabContentOptions {
  props: Readonly<TabContentProps>;
}

export const useTabContent = (options: UseTabContentOptions) => {
  const { props } = options;
  const host = useHost();
  const state = useSharedState(props);

  // Cache DOM lookup during setup — never inside computed.
  const tabElement = shallowRef<Element | null>(null);
  if (host) {
    tabElement.value = closestAcrossShadowBoundaries(host, "e-tab");
  }

  const isActive = computed<true | undefined>(() => {
    const el = tabElement.value;
    if (!el) return undefined;
    const activeValue = getTabActiveValue(el);
    return !!activeValue && activeValue.value === state.value?.value ? true : undefined;
  });

  const classes = computed(() => mergeClassProp(tabContentClasses(), props.class));

  return { classes, isActive };
};
